import { useEffect, useState } from "react";
import api, { apiError, pctIt } from "@/lib/api";
import { toast } from "sonner";
import { createPortal } from "react-dom";
import { Megaphone, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const eur = (n) => (n != null ? `€ ${Math.round(n).toLocaleString("it-IT")}` : "—");
const pct = (n) => (n == null ? "—" : `${n > 0 ? "+" : ""}${pctIt(n)}`);

function StatoSollecito({ s }) {
  if (!s) return <span className="text-[var(--muted)]">—</span>;
  const d = new Date(s.at).toLocaleDateString("it-IT");
  if (s.risposta) return <span className="text-emerald-700">{d} {s.by} → <b>{s.risposta.azione}</b>: {s.risposta.nota}</span>;
  if (s.stato === "scaduto") return <span className="text-red-700 font-semibold">{d} {s.by} · SCADUTO senza risposta</span>;
  return <span className="text-amber-700">{d} {s.by} · in attesa ({(s.direttori || []).join(", ")})</span>;
}

function NotaModal({ n, onClose, onSend }) {
  const [nota, setNota] = useState("");
  const [busy, setBusy] = useState(false);
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="fermi-nota-modal">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-md card-immo p-5">
        <h3 className="font-display font-bold mb-1">Sollecita Direttore</h3>
        <p className="text-xs text-[var(--muted)] mb-3">{n} immobili. Il Direttore del team riceverà una notifica e un task con scadenza 48 ore.</p>
        <textarea data-testid="fermi-nota" value={nota} onChange={(e) => setNota(e.target.value)} rows={3} placeholder="Nota personale (facoltativa)" className="w-full px-3 py-2 rounded-xl border border-[var(--border)] text-base sm:text-sm" />
        <div className="flex gap-2 mt-3">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-[var(--border)] text-sm">Annulla</button>
          <button data-testid="fermi-invia" disabled={busy} onClick={async () => { setBusy(true); await onSend(nota); setBusy(false); }} className="flex-1 py-2.5 rounded-xl bg-[#111111] text-white text-sm font-semibold inline-flex items-center justify-center gap-2">{busy && <Loader2 size={14} className="animate-spin" />} Invia sollecito</button>
        </div>
      </div>
    </div>, document.body);
}

export default function ImmobiliFermi({ agente }) {
  const { isDirezione: puoSollecitare } = useAuth();
  const [d, setD] = useState(null);
  const [sel, setSel] = useState({});
  const [modal, setModal] = useState(null);
  const load = () => api.get("/qualita-portafoglio/fermi", { params: { agente } }).then((r) => setD(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, [agente]); // eslint-disable-line
  const invia = async (nota) => {
    try {
      const { data } = await api.post("/qualita-portafoglio/sollecita", { ids: modal, nota });
      data.esiti.filter((e) => !e.ok).forEach((e) => toast.warning(`${d.righe.find((r) => r.id === e.id)?.titolo || e.id}: ${e.motivo}`));
      if (data.inviati) toast.success(`Sollecito inviato per ${data.inviati} immobili`);
      setModal(null); setSel({}); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  if (!d) return <div className="skeleton h-40 rounded-2xl mb-8" />;
  const scelti = Object.keys(sel).filter((k) => sel[k]);
  return (
    <div className="mb-8" data-testid="qp-fermi">
      <div className="flex items-center gap-3 flex-wrap mb-2">
        <h2 className="font-display font-bold text-base md:text-lg">Immobili fermi</h2>
        <span className="text-xs text-[var(--muted)]">sul mercato da oltre {d.soglie.giorni_mercato} giorni oppure senza visite da {d.soglie.giorni_visite} giorni (soglie in Profilo → Giorni e soglie)</span>
        {puoSollecitare && <button data-testid="fermi-sollecita-sel" disabled={!scelti.length} onClick={() => setModal(scelti)} className="ml-auto inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-red-600 text-white text-xs font-semibold disabled:opacity-40"><Megaphone size={14} /> Sollecita direttore ({scelti.length})</button>}
      </div>
      <div className="card-immo p-4 mb-3 bg-red-50 border-red-200" data-testid="fermi-totale">
        <b>Provvigioni ferme: {eur(d.totale)} su {d.n} immobili</b>
        <div className="text-xs mt-1 flex gap-4 flex-wrap">{d.per_team.map((t) => <span key={t.team} data-testid={`fermi-team-${t.team}`}>{t.team}: {eur(t.totale)} su {t.n}</span>)}</div>
      </div>
      <div className="card-immo p-0 overflow-x-auto">
        <table className="w-full text-xs min-w-[1100px]">
          <thead className="bg-[#F7F7F8] text-[10px] uppercase text-[var(--muted)]"><tr>{["", "Immobile", "Agente", "Team", "Giorni mercato", "Da ultima visita", "Visite", "Prezzo", "vs valutazione", "vs max OMI", "Provvigione azienda", "Sollecito", ""].map((h, i) => <th key={i} className="p-2 text-left">{h}</th>)}</tr></thead>
          <tbody className="divide-y divide-[var(--border)]">
            {d.righe.map((r) => (
              <tr key={r.id} data-testid={`fermi-riga-${r.id}`} className={r.ultimo_sollecito?.stato === "scaduto" ? "bg-red-100" : ""}>
                <td className="p-2"><input type="checkbox" data-testid={`fermi-check-${r.id}`} checked={!!sel[r.id]} onChange={(e) => setSel({ ...sel, [r.id]: e.target.checked })} /></td>
                <td className="p-2"><a href={`/immobili/${r.id}`} className="font-semibold hover:underline">{r.titolo}</a></td>
                <td className="p-2">{r.agente}</td><td className="p-2">{r.team}</td>
                <td className="p-2 mono">{r.giorni_mercato ?? "—"}</td><td className="p-2 mono">{r.giorni_ultima_visita ?? "mai"}</td><td className="p-2 mono">{r.visite_totali}</td>
                <td className="p-2 mono">{eur(r.prezzo)}</td><td className="p-2 mono">{pct(r.scost_valutazione)}</td><td className="p-2 mono">{pct(r.scost_omi)}</td>
                <td className="p-2 mono font-bold" data-testid={`fermi-provv-${r.id}`}>{r.provvigione != null ? eur(r.provvigione) : <span className="text-amber-700">% mancante</span>}</td>
                <td className="p-2 max-w-[260px]"><StatoSollecito s={r.ultimo_sollecito} /></td>
                <td className="p-2">{puoSollecitare && <button data-testid={`fermi-sollecita-${r.id}`} onClick={() => setModal([r.id])} className="px-2.5 py-1.5 rounded-lg border border-red-400 text-red-700 font-semibold whitespace-nowrap">Sollecita direttore</button>}</td>
              </tr>
            ))}
            {!d.righe.length && <tr><td colSpan={13} className="p-6 text-center text-[var(--muted)]">Nessun immobile fermo.</td></tr>}
          </tbody>
        </table>
      </div>
      {modal && <NotaModal n={modal.length} onClose={() => setModal(null)} onSend={invia} />}
    </div>
  );
}
